import { ChangeEvent, ClipboardEvent, FC, useContext, useEffect, useRef, useState } from 'react';
import { Stack, InputBase, styled } from '@mui/material';
import GlobalContext from '../../contexts/GlobalContext';

const PasscodeInputItem = styled(InputBase)(({ theme }) => ({
    outline: `1px solid ${theme.palette.grey[500]}`,
    flexDirection: 'column',
    width: 'calc(100%/6)',
    aspectRatio: '1/1',
    textAlign: 'center',
    color: theme.palette.common.black,
    '&.Mui-focused': {
        outline: `2px solid ${theme.palette.primary.main}`,
    },
    '& .MuiInputBase-input': {
        height: '100%',
        textAlign: 'center',
        fontSize: '20px',
        fontWeight: '600',
        padding: 0,
    },
}));

type TPasscodeInputProps = {
    onSubmit: (passcode: string) => Promise<void>;
    isDisabled?: boolean;
};

type TPasscode = [string, string, string, string, string, string];

const emptyPasscode: TPasscode = ['', '', '', '', '', ''];

const PasscodeInput: FC<TPasscodeInputProps> = (props) => {
    const { language } = useContext(GlobalContext);
    const { onSubmit, isDisabled = false } = props;
    const [passcode, setPasscode] = useState<TPasscode>([...emptyPasscode]);
    const [passcodeSubmitted, setPasscodeSubmitted] = useState(false);
    const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];

    const handlePasscodeChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
        passcodeSubmitted && setPasscodeSubmitted(false);
        // reference inputs
        const input = event.target;
        const previousInput = inputRefs[index - 1];
        const nextInput = inputRefs[index + 1];

        // update verification code
        let newPasscode: TPasscode = [...passcode];
        newPasscode[index] = input.value;
        setPasscode(newPasscode);

        // select the logical next input
        if (input.value === '') {
            if (previousInput) {
                previousInput.current.focus();
            }
        } else if (nextInput) {
            nextInput.current.focus();
        }
    };

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        // get pasted text
        const pastedCode = event.clipboardData.getData('text');
        // only update passcode if its valid
        if (pastedCode.length === 6) {
            const splitString = pastedCode.split('');
            const newPasscode: TPasscode = [...emptyPasscode];
            splitString.forEach((item, index) => {
                newPasscode[index] = item;
            });
            // set the value
            setPasscode(newPasscode);
            // focus the last input
            inputRefs[5].current.focus();
        }
    };

    const validatePasscode = () => {
        const allValidCharacters = passcode.every((character) => character !== '');
        if (passcode.length === 6 && allValidCharacters) {
            return true;
        } else {
            return false;
        }
    };

    useEffect(() => {
        const passcodeIsValid = validatePasscode();
        if (passcodeIsValid && !passcodeSubmitted) {
            onSubmit(passcode.join(''));
            setPasscodeSubmitted(true);
        }
    }, [[passcode]]);

    return (
        <Stack direction={'row'} spacing={2}>
            {[0, 1, 2, 3, 4, 5].map((itemIndex) => (
                <PasscodeInputItem
                    key={itemIndex}
                    value={passcode[itemIndex]}
                    onChange={(event) => handlePasscodeChange(event as ChangeEvent<HTMLInputElement>, itemIndex)}
                    autoFocus={itemIndex === 0}
                    inputRef={inputRefs[itemIndex]}
                    type="tel"
                    inputProps={{
                        autoCapitalize: 'false',
                        autoCorrect: 'false',
                        autoComplete: 'off',
                        spellCheck: 'false',
                        onPaste: handlePaste,
                        maxLength: 1,
                    }}
                    aria-label={`${language.common.digit} ${itemIndex}`}
                    disabled={isDisabled}
                />
            ))}
        </Stack>
    );
};

export default PasscodeInput;
